import { MessageSquare, CheckCircle2, Reply, FileSignature } from "lucide-react";
import { usePagination } from "@/lib/usePagination";

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
};

/**
 * Admin contact messages list — extracted from AdminPortal.
 * Props:
 *  - messages: array
 *  - onReply(message): open reply dialog
 *  - onQuoteFromMessage(message): switch to Quotes tab and prefill
 */
export default function MessagesTab({ messages, onReply, onQuoteFromMessage }) {
  const { pageItems, controls } = usePagination(messages, 10, "admin-messages-pager");

  return (
    <div className="border border-[#2E2E33] bg-[#1F1F25]" data-testid="admin-messages-list">
      {messages.length === 0 ? (
        <div className="p-16 text-center">
          <MessageSquare className="w-12 h-12 text-[#2E2E33] mx-auto mb-4" />
          <p className="text-[#A1A1AA]">No hay mensajes de contacto.</p>
        </div>
      ) : (
        <>
          {pageItems.map((m) => (
            <div key={m.id} className="px-4 sm:px-6 py-5 border-b border-[#2E2E33] last:border-b-0" data-testid={`message-${m.id}`}>
              <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap min-w-0">
                  <span className="font-heading font-bold text-white">{m.name}</span>
                  <span className="font-mono text-xs text-[#A1A1AA] break-all">{m.email}</span>
                  {m.company && <span className="font-mono text-xs text-[#71717A]">· {m.company}</span>}
                  {m.status === "replied" && (
                    <span className="inline-flex items-center gap-1 border border-emerald-400/40 bg-emerald-400/5 text-emerald-400 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest">
                      <CheckCircle2 className="w-3 h-3" /> Respondido
                    </span>
                  )}
                </div>
                <span className="font-mono text-[10px] text-[#71717A] uppercase">{formatDate(m.created_at)}</span>
              </div>
              {m.service && <div className="text-[#F59E0B] text-xs font-mono uppercase tracking-widest mb-2">// {m.service}</div>}
              <p className="text-[#D4D4D8] text-sm leading-relaxed whitespace-pre-wrap">{m.message}</p>
              {m.phone && <p className="font-mono text-xs text-[#71717A] mt-2">Tel: {m.phone}</p>}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => onReply(m)}
                  className="btn-primary py-2 px-4 text-[10px]"
                  data-testid={`message-reply-${m.id}`}
                >
                  <Reply className="w-3.5 h-3.5" /> Responder
                </button>
                <button
                  onClick={() => onQuoteFromMessage(m)}
                  className="inline-flex items-center gap-1.5 border border-[#F59E0B] hover:bg-[#F59E0B] hover:text-black text-[#F59E0B] px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors"
                  data-testid={`message-quote-${m.id}`}
                >
                  <FileSignature className="w-3 h-3" /> Responder con Cotización
                </button>
                <a
                  href={`mailto:${m.email}?subject=Re: Su solicitud a SGM México&body=Hola ${encodeURIComponent(m.name || "")}, `}
                  className="inline-flex items-center gap-1.5 border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]"
                  data-testid={`message-mailto-${m.id}`}
                >
                  <MessageSquare className="w-3 h-3" /> Abrir en mi correo
                </a>
              </div>
              {Array.isArray(m.replies) && m.replies.length > 0 && (
                <div className="mt-4 border-l-2 border-[#F59E0B] pl-4 space-y-2">
                  {m.replies.map((r) => (
                    <div key={r.id} className="text-xs">
                      <div className="font-mono text-[10px] text-[#71717A] uppercase tracking-widest mb-1">
                        // Respuesta enviada el {formatDate(r.at)} por {r.by}
                      </div>
                      <p className="text-[#D4D4D8] whitespace-pre-wrap text-sm">{r.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {controls}
        </>
      )}
    </div>
  );
}
